/**
 * Small helpers shared by the search composites in this folder (CityCombobox,
 * PropertyTypePicker, RangeFilterGrid, PresetNumberField). Not a public API:
 * import the composites, not these.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { toLatinDigits } from '../../utils/arabicNormalize';

/** Join class names, skipping falsy parts. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

type Vars = Record<string, string | number>;

function interpolate(template: string, vars?: Vars): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) =>
    vars && vars[name] !== undefined ? String(vars[name]) : '',
  );
}

export interface NsText {
  /** `t(key)` in one namespace that always returns a string. */
  text: (key: string, defaultValue: string, vars?: Vars) => string;
  /** The UI language is Arabic (the site default when unknown). */
  isArabic: boolean;
}

/**
 * Translation for one namespace that never hands React an object.
 *
 * The shared test setup mocks `t(key, fallback)` as `fallback || key`, so a plain
 * `t(key, { defaultValue })` returns the options OBJECT there. Falling back to the
 * interpolated English default keeps every page that mounts these composites
 * renderable in its own tests (the same trick as base/primitiveUtils `useUiText`).
 */
export function useNsText(ns: string): NsText {
  const { t, i18n } = useTranslation(ns);
  const text = useCallback(
    (key: string, defaultValue: string, vars?: Vars): string => {
      const out: unknown = t(key, { defaultValue, ...vars });
      if (typeof out === 'string' && out !== key && out !== `${ns}:${key}`) return out;
      return interpolate(defaultValue, vars);
    },
    [t, ns],
  );
  const language = typeof i18n?.language === 'string' ? i18n.language : '';
  return { text, isArabic: language ? language.startsWith('ar') : true };
}

// ── Media queries ───────────────────────────────────────────────────────────

function canMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

/** True while `query` matches (false where matchMedia is missing, e.g. jsdom). */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!canMatchMedia()) return () => {};
      const mql = window.matchMedia(query);
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
      }
      // Safari before 14.
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    },
    [query],
  );
  const getSnapshot = () => (canMatchMedia() ? window.matchMedia(query).matches : false);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

// ── Typed numbers ───────────────────────────────────────────────────────────

/** Longest number a range field accepts (999,999,999,999), well inside exact doubles. */
export const MAX_AMOUNT_DIGITS = 12;

/**
 * What a person typed or pasted, as a plain Latin digit string: Arabic-Indic digits
 * read as 0-9, separators and letters dropped, leading zeros trimmed ("007" → "7",
 * "000" → "0"), at most `maxDigits` long. '' when there are no digits.
 */
export function toDigitString(text: string, maxDigits = MAX_AMOUNT_DIGITS): string {
  const digits = toLatinDigits(String(text ?? '')).replace(/\D/g, '');
  return digits.replace(/^0+(?=\d)/, '').slice(0, Math.max(0, maxDigits));
}

/** How many digits (Latin or Arabic-Indic) `text` holds. */
export function countDigits(text: string): number {
  return toLatinDigits(text).replace(/\D/g, '').length;
}

/** The caret index in `text` right after its `n`-th digit (0 → the start). */
export function indexAfterDigits(text: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (/\d/.test(text[i])) {
      seen += 1;
      if (seen === n) return i + 1;
    }
  }
  return text.length;
}
